import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Activity,
  Send,
  Eye,
  MousePointerClick,
  CheckCircle2,
  TrendingUp,
  AlertTriangle,
  Filter,
} from "lucide-react";

const WINDOWS = [
  { value: 7,   label: "7d"  },
  { value: 30,  label: "30d" },
  { value: 90,  label: "90d" },
  { value: 365, label: "1y"  },
];

const TONE_TO_HEX = {
  emerald: "#10B981",
  amber:   "#F59E0B",
  red:     "#DC2626",
  slate:   "#64748B",
};

export default function AdminEmailFunnels() {
  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(90);

  const refresh = useCallback(async (days = windowDays) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/email-funnels", { params: { days } });
      setFunnels(r.data.funnels || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load funnels");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh(windowDays);
  }, [refresh, windowDays]);

  return (
    <div className="space-y-6" data-testid="admin-email-funnels">
      {/* Hero */}
      <div className="card p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center flex-shrink-0">
            <Activity size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-extrabold text-2xl tracking-tight text-[var(--ink)]">Email Funnels</h2>
            <p className="text-[var(--ink-2)] text-sm mt-1 leading-relaxed">
              Conversion measurement for each lifecycle email: <strong className="text-[var(--ink)]">sent →
              delivered → opened → clicked → converted</strong>. "Converted" is judged against each user's
              <em> current</em> subscription status — so a dunning email "wins" if the customer is back to good standing.
            </p>
          </div>
          <div className="flex items-center gap-1 bg-[var(--bg)] rounded-lg p-1 border border-[var(--line)]">
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                onClick={() => setWindowDays(w.value)}
                className={
                  "px-3 py-1.5 rounded-md text-xs font-semibold transition-all " +
                  (w.value === windowDays
                    ? "bg-[var(--ink)] text-white"
                    : "text-[var(--ink-2)] hover:bg-white")
                }
                data-testid={`funnels-window-${w.value}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <div className="card p-12 flex items-center justify-center text-[var(--ink-2)]">
          <Loader2 size={20} className="animate-spin mr-3" /> Loading funnels…
        </div>
      )}

      {!loading && funnels.map((f) => (
        <FunnelCard key={f.kind} funnel={f} />
      ))}

      {!loading && funnels.length === 0 && (
        <div className="card p-12 text-center text-[var(--ink-2)]">
          No funnel data yet. Send a few lifecycle emails and they'll show up here.
        </div>
      )}
    </div>
  );
}

function FunnelCard({ funnel }) {
  const tone = TONE_TO_HEX[funnel.tone] || TONE_TO_HEX.emerald;
  const sent = funnel.sent || 0;
  const isEmpty = sent === 0;

  // The 5 funnel stages, in order
  const stages = [
    { key: "sent",       label: "Sent",       value: sent,             icon: Send,             rate: 100 },
    { key: "delivered",  label: "Delivered",  value: funnel.delivered, icon: CheckCircle2,     rate: funnel.rates.delivery },
    { key: "opened",     label: "Opened",     value: funnel.opened,    icon: Eye,              rate: funnel.rates.open },
    { key: "clicked",    label: "Clicked",    value: funnel.clicked,   icon: MousePointerClick,rate: funnel.rates.click },
    { key: "converted",  label: "Converted",  value: funnel.converted, icon: TrendingUp,       rate: funnel.rates.conversion },
  ];

  // Drop-off between stages
  const dropoffs = [];
  for (let i = 1; i < stages.length; i++) {
    const prev = stages[i - 1].value;
    const curr = stages[i].value;
    const lost = Math.max(0, prev - curr);
    dropoffs.push({
      from: stages[i - 1].label,
      to:   stages[i].label,
      lost,
      lostPct: prev ? Math.round((lost / prev) * 100) : 0,
    });
  }

  return (
    <div className="card p-6" data-testid={`funnel-${funnel.kind}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-lg text-[var(--ink)]">{funnel.label}</h3>
            <span className="badge" style={{ background: "#F1F5F9", color: "#64748B" }}>
              {funnel.kind}
            </span>
            <span
              className="badge"
              style={{ background: `${tone}1A`, color: tone, borderColor: `${tone}40` }}
            >
              {funnel.stage}
            </span>
          </div>
          <p className="text-[var(--ink-2)] text-sm mt-1 leading-relaxed">
            <strong className="text-[var(--ink)]">Goal:</strong> {funnel.goal}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold">Conversion rate</div>
          <div className="font-display font-bold text-3xl" style={{ color: isEmpty ? "var(--ink-3)" : tone }}>
            {funnel.rates.conversion.toFixed(1)}%
          </div>
        </div>
      </div>

      {isEmpty ? (
        <div className="mt-4 p-6 bg-[var(--bg)] border border-dashed border-[var(--line)] rounded-lg text-center text-[var(--ink-3)] text-sm">
          No emails sent in this window. Funnel starts populating as soon as the first one fires.
        </div>
      ) : (
        <>
          {/* Stage bars */}
          <div className="mt-5 space-y-2">
            {stages.map((s, i) => {
              const Icon = s.icon;
              const widthPct = Math.max(2, Math.min(100, (s.value / sent) * 100));
              const isFinal = i === stages.length - 1;
              return (
                <div key={s.key} className="flex items-center gap-3" data-testid={`funnel-stage-${funnel.kind}-${s.key}`}>
                  <div className="w-28 flex items-center gap-2 text-xs font-semibold text-[var(--ink)]">
                    <Icon size={13} />
                    {s.label}
                  </div>
                  <div className="flex-1 relative h-7 bg-[var(--bg)] rounded-md border border-[var(--line)] overflow-hidden">
                    <div
                      className="h-full rounded-md transition-all flex items-center px-3"
                      style={{
                        width: `${widthPct}%`,
                        background: isFinal ? tone : "var(--ink)",
                        opacity: isFinal ? 1 : (0.95 - i * 0.12),
                      }}
                    >
                      <span className="text-xs font-bold text-white whitespace-nowrap">
                        {s.value.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="w-16 text-right text-sm font-display font-bold text-[var(--ink)]">
                    {s.rate.toFixed(1)}%
                  </div>
                </div>
              );
            })}
          </div>

          {/* Drop-offs */}
          <div className="mt-5 pt-4 border-t border-[var(--line)]">
            <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] font-semibold mb-3 flex items-center gap-1.5">
              <Filter size={11} /> Drop-off between stages
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {dropoffs.map((d) => (
                <div key={`${d.from}->${d.to}`} className="bg-[var(--bg)] rounded-md p-3 border border-[var(--line)]">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold">
                    {d.from} → {d.to}
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-display font-bold text-lg text-[var(--ink)]">
                      -{d.lost.toLocaleString()}
                    </span>
                    <span className="text-xs text-[var(--ink-3)]">({d.lostPct}% lost)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Click → conversion insight */}
          {funnel.clicked > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-[var(--primary)]/8 border border-[var(--primary)]/30 text-sm text-[var(--ink)] flex items-center gap-2">
              <TrendingUp size={14} className="text-[var(--primary-dark)] flex-shrink-0" />
              <span>
                Click → conversion:{" "}
                <strong className="text-[var(--primary-dark)]">
                  {funnel.rates.click_to_conversion.toFixed(1)}%
                </strong>{" "}
                of users who clicked ended in good standing — the call-to-action is{" "}
                {funnel.rates.click_to_conversion >= 50 ? "working hard" : "leaking — try a different CTA"}.
              </span>
            </div>
          )}

          {funnel.bounced > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle size={14} className="flex-shrink-0" />
              <span>
                <strong>{funnel.bounced}</strong> bounce{funnel.bounced !== 1 ? "s" : ""} in this window.
                Bounced addresses don't reach the inbox at all — check your Resend dashboard.
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
